import { Link } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { AlertCircle, LoaderCircle, Pencil, Plus, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { canManageFactory } from "@/app/guards";
import { useObraId } from "@/app/useObraId";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { ExportButton } from "@/components/ui/ExportButton";
import { type CampoFiltro, FiltrosBar } from "@/components/ui/filtros-bar";
import { ImportButton } from "@/components/ui/ImportButton";
import { Modal } from "@/components/ui/modal";
import { type Area, useAreas, useCreateArea, useDeleteArea, useUpdateArea } from "@/lib/areas";
import { useMe } from "@/lib/auth";
import { AVISO_FILTRADO_LOCAL, mensajeErrorLista, useFiltrosURL } from "@/lib/filtros";
import { CLAVES_AREAS } from "@/lib/filtros/areas";

// Campos de la barra de filtros de áreas (#247). La entidad NO tiene columna `activo`, así
// que el único filtro es el texto libre sobre nombre y descripción.
const CAMPOS_FILTRO: CampoFiltro[] = [
  { tipo: "texto", nombre: "q", etiqueta: "Buscar", placeholder: "Buscar áreas…" },
];

import { AreaForm, type AreaFormValues } from "./AreaForm";

// toInput normaliza los valores del formulario al payload del API (descripción vacía
// ⇒ null).
function toInput(values: AreaFormValues) {
  const descripcion = values.descripcion.trim();
  return { nombre: values.nombre.trim(), descripcion: descripcion === "" ? null : descripcion };
}

/**
 * AreasPage es la lista de áreas con sus métricas (equipos y procedimientos) y el CRUD
 * completo (alta/edición en modal, borrado con confirmación). Es la página de
 * referencia del patrón de maestro de fábrica que replican #28–#31. La tabla usa el
 * DataTable de react-table (#34) SIN buscador en memoria (`sinBuscador`): el texto libre lo
 * filtra el SERVIDOR desde la FiltrosBar (#247) y la tabla solo ordena y pagina, así que sus
 * `accessorFn` alimentan la celda, la ordenación o ambas, nunca una búsqueda (#346). Además
 * ofrece exportación xlsx (áreas tiene endpoint de exportación).
 */
export function AreasPage() {
  const { data: me } = useMe();
  const obraId = useObraId();
  const puedeGestionar = me != null && canManageFactory(me, obraId);

  // Los filtros viven en la URL (#247): compartible, recargable y con el servidor filtrando.
  const { filtros, setFiltros } = useFiltrosURL(CLAVES_AREAS);
  const {
    data: areas,
    isPending,
    isError,
    baseVacia,
    avisoSinConexion,
    codigoError,
    camposError,
  } = useAreas(obraId, filtros);
  const filas = areas ?? [];
  // «Aún no hay áreas» solo con el catálogo vacío de verdad; si los filtros no dejan
  // ninguna, lo dice la fila vacía de la tabla.
  const crear = useCreateArea(obraId);
  const editar = useUpdateArea(obraId);
  const borrar = useDeleteArea(obraId);

  // Estado de los diálogos: alta ("nueva"), edición (área) o borrado (área).
  const [formState, setFormState] = useState<
    { mode: "nueva" } | { mode: "editar"; area: Area } | null
  >(null);
  const [aBorrar, setABorrar] = useState<Area | null>(null);

  const columns = useMemo<ColumnDef<Area, unknown>[]>(() => {
    const cols: ColumnDef<Area, unknown>[] = [
      {
        accessorKey: "nombre",
        header: "Nombre",
        cell: ({ row }) => (
          <span className="font-medium text-slate-900">{row.original.nombre}</span>
        ),
      },
      {
        id: "descripcion",
        accessorFn: (a) => a.descripcion ?? "—",
        header: "Descripción",
        cell: ({ getValue }) => <span className="text-slate-500">{getValue<string>()}</span>,
        meta: { className: "hidden sm:table-cell" },
      },
      {
        accessorKey: "num_equipos",
        header: "Equipos",
        meta: { className: "text-right", stopRowClick: true },
        cell: ({ row }) => (
          <Link
            to="/obras/$obraId/equipos"
            params={{ obraId: String(obraId) }}
            search={{ area: String(row.original.id) }}
            aria-label={`Ver equipos del área ${row.original.nombre}`}
            className="font-medium text-primary-700 tabular-nums hover:underline"
          >
            {row.original.num_equipos} {row.original.num_equipos === 1 ? "equipo" : "equipos"}
          </Link>
        ),
      },
      {
        accessorKey: "num_procedimientos",
        header: "Procedimientos",
        meta: { className: "text-right" },
        cell: ({ row }) => <span className="tabular-nums">{row.original.num_procedimientos}</span>,
      },
    ];
    if (puedeGestionar) {
      cols.push({
        id: "acciones",
        header: () => <span className="sr-only">Acciones</span>,
        enableSorting: false,
        enableGlobalFilter: false,
        meta: { className: "text-right", stopRowClick: true },
        cell: ({ row }) => (
          <div className="flex justify-end gap-1">
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Editar ${row.original.nombre}`}
              onClick={() => setFormState({ mode: "editar", area: row.original })}
            >
              <Pencil className="h-5 w-5" aria-hidden="true" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Eliminar ${row.original.nombre}`}
              onClick={() => setABorrar(row.original)}
            >
              <Trash2 className="h-5 w-5 text-red-600" aria-hidden="true" />
            </Button>
          </div>
        ),
      });
    }
    return cols;
  }, [obraId, puedeGestionar]);

  function cerrarForm() {
    setFormState(null);
  }

  async function onSubmitForm(values: AreaFormValues) {
    if (formState?.mode === "editar") {
      await editar.mutateAsync({ id: formState.area.id, input: toInput(values) });
    } else {
      await crear.mutateAsync(toInput(values));
    }
    cerrarForm();
  }

  async function confirmarBorrado() {
    if (!aBorrar) {
      return;
    }
    await borrar.mutateAsync(aBorrar.id);
    setABorrar(null);
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col">
          <h1 className="font-bold text-2xl text-slate-900 tracking-tight">Áreas</h1>
          <p className="text-base text-slate-500">Áreas de fábrica y sus activos.</p>
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          <ImportButton tipo="areas" etiqueta="áreas" obraId={obraId} />
          <ExportButton tipo="areas" obraId={obraId} filtros={filtros} />
          {puedeGestionar ? (
            <Button onClick={() => setFormState({ mode: "nueva" })}>
              <Plus className="h-5 w-5" aria-hidden="true" />
              Nueva área
            </Button>
          ) : null}
        </div>
      </div>

      {isError ? (
        <Alert variant="destructive">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>
            {mensajeErrorLista(
              { codigoError, camposError },
              "No se pudieron cargar las áreas. Inténtalo de nuevo.",
            )}
          </span>
        </Alert>
      ) : null}

      {isPending ? (
        <div className="flex items-center justify-center gap-2 py-10 text-slate-500">
          <LoaderCircle className="h-6 w-6 animate-spin" aria-hidden="true" />
          Cargando áreas…
        </div>
      ) : null}

      {!isPending && !isError && baseVacia ? (
        <div className="rounded-2xl border border-slate-200 border-dashed bg-white px-4 py-12 text-center text-slate-500">
          Aún no hay áreas.{puedeGestionar ? " Crea la primera con «Nueva área»." : ""}
        </div>
      ) : null}

      {!baseVacia ? (
        <>
          <FiltrosBar
            campos={CAMPOS_FILTRO}
            valor={filtros}
            onChange={setFiltros}
            aviso={avisoSinConexion ? AVISO_FILTRADO_LOCAL : undefined}
          />
          {!isPending ? (
            <DataTable
              columns={columns}
              data={filas}
              sinBuscador
              onRowClick={
                puedeGestionar ? (area) => setFormState({ mode: "editar", area }) : undefined
              }
            />
          ) : null}
        </>
      ) : null}

      {/* Alta / edición */}
      <Modal
        open={formState !== null}
        onClose={cerrarForm}
        title={formState?.mode === "editar" ? "Editar área" : "Nueva área"}
      >
        {formState !== null ? (
          <AreaForm
            defaultValues={
              formState.mode === "editar"
                ? {
                    nombre: formState.area.nombre,
                    descripcion: formState.area.descripcion ?? "",
                  }
                : { nombre: "", descripcion: "" }
            }
            submitLabel={formState.mode === "editar" ? "Guardar cambios" : "Crear área"}
            pending={crear.isPending || editar.isPending}
            onSubmit={onSubmitForm}
            onCancel={cerrarForm}
          />
        ) : null}
      </Modal>

      {/* Confirmación de borrado */}
      <Modal open={aBorrar !== null} onClose={() => setABorrar(null)} title="Eliminar área">
        <p className="text-base text-slate-600">
          ¿Seguro que quieres eliminar el área <strong>{aBorrar?.nombre}</strong>? Esta acción la da
          de baja.
        </p>
        {borrar.isError ? (
          <Alert variant="destructive">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
            <span>No se pudo eliminar el área. Inténtalo de nuevo.</span>
          </Alert>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setABorrar(null)} disabled={borrar.isPending}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={() => void confirmarBorrado()}
            disabled={borrar.isPending}
          >
            {borrar.isPending ? (
              <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
            ) : (
              <Trash2 className="h-5 w-5" aria-hidden="true" />
            )}
            Eliminar
          </Button>
        </div>
      </Modal>
    </div>
  );
}
